/* eslint-disable @nx/enforce-module-boundaries */
import React, { useMemo } from 'react';
import clsx from 'clsx';
import { CN_RATING_TOOLTIP, PropertyRatingContainer, PropertyReview, TOOLTIP_POSITION } from '@marriott/mi-ui-library';
import { formatNumberByLocale } from '../../utils/utils';
import { isReviewCountHidden } from '@marriott/mi-shop-components';
import * as Styled from './MPropertyReviewRating.styles';

interface MPropertyReviewRatingProps {
  ratingInfo: {
    isMax: boolean;
    reviewUrl: string;
    reviewTitle: string;
    ratingValue: number;
    hasRating: boolean;
    reviewCountValue: number;
    reviewsLabel?: string;
  };
}

const getStarIconClass = (rating: number): string => {
  return rating > 2.5
    ? 'star-icon-container icon-alt icon-star-fill'
    : 'star-icon-container icon-alt icon-star-half-fill';
};

export const MPropertyReviewRating: React.FC<MPropertyReviewRatingProps> = ({ ratingInfo }) => {
  const { isMax, reviewUrl, reviewTitle, ratingValue, hasRating, reviewCountValue } = ratingInfo;
  // Prefer document locale so displayed numbers match the page language.
  const currentLocale = useMemo(() => {
    if (typeof document !== 'undefined' && document.documentElement?.lang) {
      return document.documentElement.lang;
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return 'en-US';
  }, []);

  const ratingContainer = isReviewCountHidden(currentLocale)
    ? 'rating-container is-review-count-hidden'
    : 'rating-container';
  const hasReviewSummary = reviewCountValue > 0 && hasRating;

  // Match PropertyCard behavior: don't show ratings section for max-occupancy cards.
  if (isMax || !hasReviewSummary) return null;

  return (
    <PropertyRatingContainer
      propertyReviewUrl={reviewUrl}
      className={clsx(ratingContainer, 'custom_click_track')}
      title={reviewTitle}
      aria-label={reviewTitle}
      disabled={isReviewCountHidden(currentLocale)}
      tooltipPosition={TOOLTIP_POSITION.right}
      toolTipContent={CN_RATING_TOOLTIP}
      target="_blank"
      rel="noreferrer"
    >
      {hasReviewSummary && (
        <Styled.RatingContainer>
          <div className="m-ratings">
            <div className="m-ratings-stars">
              <span className={getStarIconClass(ratingValue)} />
            </div>
            <span className="star-number-container">{formatNumberByLocale(ratingValue, currentLocale)}</span>
            <PropertyReview
              isVisible={reviewCountValue > 0 && !isReviewCountHidden(currentLocale)}
              className="review-number-container"
            >
              <Styled.ReviewsCountContainer>
                ({formatNumberByLocale(reviewCountValue, currentLocale)})
              </Styled.ReviewsCountContainer>
            </PropertyReview>
          </div>
        </Styled.RatingContainer>
      )}
    </PropertyRatingContainer>
  );
};
